import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation } from "react-router-dom";
import { mdiChevronRight } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";

import { ISideBarType } from "models/Base";
import styles from "./style.module.scss";

type SideBarNavItemProps = {
  item: ISideBarType;
  isSideBarExpanded: boolean;
};

export default function SideBarNavItem({ item, isSideBarExpanded }: Readonly<SideBarNavItemProps>) {
  const { pathname } = useLocation();
  const isActive =
    item.link === pathname || !!item.children?.find((child) => pathname.includes(child.link!));

  const [showChildren, setShowChildren] = useState(isActive);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [flyoutPos, setFlyoutPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const itemRef = useRef<HTMLLIElement>(null);
  const closeTimer = useRef<number | null>(null);

  useEffect(() => {
    if (isActive) {
      setShowChildren(isActive);
    }
  }, [isActive]);

  useEffect(() => {
    if (isSideBarExpanded && flyoutOpen) setFlyoutOpen(false);
  }, [isSideBarExpanded, flyoutOpen]);

  useLayoutEffect(() => {
    if (!flyoutOpen || !itemRef.current) return;
    const itemRect = itemRef.current.getBoundingClientRect();
    const sidebar = itemRef.current.closest("[data-sidebar-root]");
    const sidebarRight = sidebar ? sidebar.getBoundingClientRect().right : itemRect.right;
    setFlyoutPos({ top: itemRect.top, left: sidebarRight - 56 });
  }, [flyoutOpen]);

  const hasChildren = !!item.children?.length;
  const canShowFlyout = !isSideBarExpanded && hasChildren;

  const openFlyout = () => {
    if (!canShowFlyout) return;
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setFlyoutOpen(true);
  };

  const scheduleClose = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setFlyoutOpen(false), 120);
  };

  const targetLink = item.link ?? item.children?.[0]?.link ?? "/";

  return (
    <li
      ref={itemRef}
      onMouseEnter={openFlyout}
      onMouseLeave={scheduleClose}
      onFocus={openFlyout}
      onClick={openFlyout}>
      <NavLink to={targetLink}>
        <div className={clsx(styles.item, isActive ? "text-sidebar" : "text-subtitle")}>
          {item.icon && <Icon path={item.icon} className="flex-none" />}
          {isSideBarExpanded && (
            <span className="flex-1 text-base whitespace-nowrap">{item.title}</span>
          )}
          {hasChildren ? (
            <button
              onClick={(event) => {
                if (!isSideBarExpanded) {
                  openFlyout();
                } else {
                  setShowChildren((prev) => !prev);
                }
                event.preventDefault();
              }}>
              <Icon
                path={mdiChevronRight}
                className={clsx(
                  styles.arrow,
                  showChildren && !isSideBarExpanded && "!hidden",
                  isActive ? "text-sidebar" : "text-subtitle",
                  showChildren && "rotate-90"
                )}
              />
            </button>
          ) : null}
        </div>
      </NavLink>

      {hasChildren && showChildren && isSideBarExpanded ? (
        <ul className="flex flex-col pl-10">
          {item.children!.map((subItem) => (
            <NavLink
              key={subItem.link}
              to={subItem.link ?? ""}
              className={clsx(
                styles.item,
                pathname.includes(subItem.link!) ? "text-sidebar" : "text-subtitle"
              )}>
              {subItem.title}
            </NavLink>
          ))}
        </ul>
      ) : null}

      {canShowFlyout && flyoutOpen
        ? createPortal(
            <div
              onMouseEnter={openFlyout}
              onMouseLeave={scheduleClose}
              style={{ position: "fixed", top: flyoutPos.top, left: flyoutPos.left, zIndex: 9999 }}
              className={styles.flyout}>
              <div className={styles.flyoutTitle}>
                {item.icon && <Icon path={item.icon} className="flex-none size-4" />}
                <span>{item.title}</span>
              </div>
              <ul className="flex flex-col">
                {item.children!.map((subItem) => (
                  <NavLink
                    key={subItem.link}
                    to={subItem.link ?? ""}
                    onClick={() => setFlyoutOpen(false)}
                    className={clsx(
                      styles.flyoutItem,
                      pathname.includes(subItem.link!) ? "text-sidebar" : "text-subtitle"
                    )}>
                    {subItem.title}
                  </NavLink>
                ))}
              </ul>
            </div>,
            document.body
          )
        : null}
    </li>
  );
}
